import { useState, useEffect } from "react";
import { ensureApiKey } from "./apiKeyUtils";
import { apiFetch } from "../config/apiConfig";

/**
 * Webhook Utilities for BalanceApp Admin Operations
 * Handles long-running operations with webhook notifications and job monitoring
 */

/**
 * JobMonitor class for tracking long-running operations
 */
export class JobMonitor {
  constructor(jobId, onStatusUpdate, onComplete, onError) {
    this.jobId = jobId;
    this.onStatusUpdate = onStatusUpdate;
    this.onComplete = onComplete;
    this.onError = onError;
    this.polling = false;
    this.pollInterval = null;
  }

  startMonitoring(intervalMs = 2000) {
    if (this.polling) return;

    this.polling = true;
    this.pollInterval = setInterval(async () => {
      try {
        const response = await apiFetch(`/admin/jobs/${this.jobId}/status`);

        if (response.ok) {
          const status = await response.json();
          this.onStatusUpdate(status);

          if (status.completed) {
            this.stopMonitoring();
            this.onComplete(status);
          } else if (status.failed) {
            this.stopMonitoring();
            this.onError(status.error || "Job failed");
          }
        }
      } catch (error) {
        this.stopMonitoring();
        this.onError(error.message);
      }
    }, intervalMs);
  }

  stopMonitoring() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.polling = false;
  }
}

/**
 * Execute a webhook operation with job monitoring
 */
export async function executeWebhookOperation(
  endpoint,
  options = {},
  onStatusUpdate = null,
  onComplete = null,
  onError = null
) {
  try {
    const response = await apiFetch(endpoint, {
      method: "POST",
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();

    if (result.jobId && onStatusUpdate) {
      // Start monitoring the job
      const monitor = new JobMonitor(
        result.jobId,
        onStatusUpdate,
        onComplete,
        onError
      );
      monitor.startMonitoring();
      return { success: true, jobId: result.jobId, monitor };
    }

    return { success: true, result };
  } catch (error) {
    if (onError) onError(error.message);
    return { success: false, error: error.message };
  }
}

/**
 * React hook for webhook operations
 */
export function useWebhookOperation() {
  const [status, setStatus] = useState("idle"); // idle, running, success, error
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [monitor, setMonitor] = useState(null);

  const execute = async (endpoint, options = {}) => {
    setStatus("running");
    setProgress(null);
    setError(null);
    setResult(null);
    setJobId(null);

    const operation = await executeWebhookOperation(
      endpoint,
      options,
      (statusUpdate) => {
        setProgress(statusUpdate);
      },
      (completionResult) => {
        setStatus("success");
        setResult(completionResult);
      },
      (errorMessage) => {
        setStatus("error");
        setError(errorMessage);
      }
    );

    if (operation.success) {
      if (operation.jobId) {
        setJobId(operation.jobId);
        setMonitor(operation.monitor);
      } else {
        setStatus("success");
        setResult(operation.result);
      }
    } else {
      setStatus("error");
      setError(operation.error);
    }

    return operation;
  };

  const reset = () => {
    if (monitor) {
      monitor.stopMonitoring();
    }
    setStatus("idle");
    setProgress(null);
    setError(null);
    setResult(null);
    setJobId(null);
    setMonitor(null);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (monitor) {
        monitor.stopMonitoring();
      }
    };
  }, [monitor]);

  return {
    status,
    progress,
    error,
    result,
    jobId,
    execute,
    reset,
    isRunning: status === "running",
    isSuccess: status === "success",
    isError: status === "error",
    isIdle: status === "idle",
  };
}

/**
 * Get authentication token, preferring API key over session token
 */
async function getAuthToken() {
  const sessionToken = localStorage.getItem("sessionToken");

  try {
    // Try to get or create an API key
    const apiKey = await ensureApiKey(sessionToken);
    return apiKey;
  } catch (error) {
    // Fall back to session token
    if (sessionToken) {
      return sessionToken;
    } else {
      throw new Error("No authentication method available");
    }
  }
}

/**
 * Get webhook logs
 */
export async function getWebhookLogs(limit = 100) {
  try {
    const response = await apiFetch(`/admin/webhooks/logs?limit=${limit}`);

    if (response.ok) {
      const data = await response.json();
      return { success: true, logs: data.logs || [] };
    } else {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || "Failed to fetch webhook logs",
      };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Get active jobs
 */
export async function getActiveJobs() {
  try {
    const response = await apiFetch("/admin/jobs/active");

    if (response.ok) {
      const data = await response.json();
      return { success: true, jobs: data.jobs || [] };
    } else {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || "Failed to fetch active jobs",
      };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}

/**
 * Get job history
 */
export async function getJobHistory(limit = 50) {
  try {
    const response = await apiFetch(`/admin/jobs/history?limit=${limit}`);

    if (response.ok) {
      const data = await response.json();
      return { success: true, jobs: data.jobs || [] };
    } else {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || "Failed to fetch job history",
      };
    }
  } catch (error) {
    return { success: false, error: error.message };
  }
}
